import { useEffect, useState } from "react";

/* ─── Dark mode detection (watches Tailwind's `dark` class on <html>) ─── */
export const useDark = () => {
  const [dark, setDark] = useState(
    () =>
      typeof document !== "undefined" &&
      document.documentElement.classList.contains("dark"),
  );
  useEffect(() => {
    const el = document.documentElement;
    const obs = new MutationObserver(() => {
      setDark(el.classList.contains("dark"));
    });
    obs.observe(el, { attributes: true, attributeFilter: ["class"] });
    return () => obs.disconnect();
  }, []);
  return dark;
};

/* ─── Theme tokens ───────────────────────────────────────────────────────────
 *
 * All inline-style values resolve to the VN semantic token palette.
 * CSS custom properties can't be used in canvas/inline styles, so we carry
 * the resolved rgba equivalents here — matching exactly what tailwind.config
 * emits for each token in each mode.
 *
 * Dark palette reference  → surface:#121C2E · popUpbg:#1A283E
 *                           surfaceBorder:#2A3C50 · inputBorder:#304658
 *                           selectedSection:#142234 · innerBg:#101828
 *                           mainText:rgba(225,232,240,0.90)
 *                           mutedText:rgba(148,163,184,0.72)
 *
 * Light palette reference → surface:#e4eaf2 · popUpbg:#edf1f7
 *                           surfaceBorder:#a0afc2 · inputBorder:#788698
 *                           selectedSection:#bfcadb · inputBg:#dfe6ef
 *                           mainText:rgba(15,23,42,0.88)
 *                           mutedText:rgba(100,116,139,0.80)
 *
 * velBlue (#0EA5E9) is mode-invariant for backgrounds/borders.
 * Active text in light mode uses the darker sky variant (#0284c7) for
 * contrast against the cool blue-gray canvas.
 * ───────────────────────────────────────────────────────────────────────── */

export const getTheme = (dark) =>
  dark
    ? {
        // ── Surface ──────────────────────────────────────────────────────────
        // surface dark (#121C2E) at 95% — lifted navy, matches bg-surface/95
        barBg: "rgba(18,28,46,0.95)",
        // surfaceBorder dark (#2A3C50) at 60% — structural border, not velBlue tint
        barBorder: "rgba(42,60,80,0.60)",
        // shadow-login-panel dark + velBlue glow at 12%
        barShadow:
          "0 12px 50px rgba(0,0,0,0.55), 0 0 1px rgba(14,165,233,0.12)",
        // surfaceBorder dark at 45% — quiet section separator
        divider: "rgba(42,60,80,0.45)",

        // ── Inputs & buttons ─────────────────────────────────────────────────
        // innerBg dark (#101828) at 60% — matches bg-innerBg/60
        inputBg: "rgba(16,24,40,0.60)",
        // inputBorder dark (#304658) at 50% — VN input border family
        inputBorder: "rgba(48,70,88,0.50)",
        // mainText dark
        inputText: "rgba(225,232,240,0.90)",
        // mutedText dark at 50% — placeholder, acceptable per VN spec
        inputPlaceholder: "rgba(148,163,184,0.50)",

        // ── Pills (inactive filter buttons) ──────────────────────────────────
        // innerBg dark at 60%
        pillBg: "rgba(16,24,40,0.60)",
        // surfaceBorder dark at 35%
        pillBorder: "rgba(42,60,80,0.35)",
        // mutedText dark at 60%
        pillText: "rgba(148,163,184,0.60)",
        // mutedText dark at 50%
        pillIconColor: "rgba(148,163,184,0.50)",

        // ── Active states ─────────────────────────────────────────────────────
        // velBlue at 15%
        activeBg: "rgba(14,165,233,0.15)",
        // velBlue at 30% — matches node.activeBorder in GraphThemes
        activeBorder: "rgba(14,165,233,0.30)",
        // velBlue at 90%
        activeText: "rgba(14,165,233,0.90)",
        // velBlue at 85%
        activeIcon: "rgba(14,165,233,0.85)",

        // ── Hover ─────────────────────────────────────────────────────────────
        // innerBg dark at 80%
        hoverBg: "rgba(16,24,40,0.80)",
        // velBlueSecond-ish at 25% — matches surface.borderHover in GraphThemes
        hoverBorder: "rgba(56,189,248,0.25)",
        // velBlue glow at 12% — matches node.circleShadow family
        hoverShadow: "0 0 8px rgba(14,165,233,0.12)",
        hoverBtnShadow: "0 0 12px rgba(14,165,233,0.15)",

        // ── Text ──────────────────────────────────────────────────────────────
        // mainText dark
        textPrimary: "rgba(225,232,240,0.90)",
        // mutedText dark at 70%
        textSecondary: "rgba(148,163,184,0.70)",
        // mutedText dark at 40%
        textMuted: "rgba(148,163,184,0.40)",

        // ── Dropdown ─────────────────────────────────────────────────────────
        // popUpbg dark (#1A283E) at 95% — matches bg-popUpbg/90+
        dropBg: "rgba(26,40,62,0.95)",
        // surfaceBorder dark (#2A3C50) at 90%
        dropBorder: "rgba(42,60,80,0.90)",
        // shadow-login-panel dark
        dropShadow:
          "0 12px 50px rgba(0,0,0,0.55), 0 0 1px rgba(14,165,233,0.12)",
        // selectedSection dark (#142234) at 80% — matches hover:bg-selectedSection
        dropHover: "rgba(20,34,52,0.80)",

        // ── Icon button default ───────────────────────────────────────────────
        // mutedText dark at 60%
        iconDefault: "rgba(148,163,184,0.60)",

        // ── Clear / destructive button ────────────────────────────────────────
        // inactiveCase / errorText family — red
        clearText: "rgba(248,113,113,0.90)",
        clearBg: "rgba(239,68,68,0.10)",
        clearBgHover: "rgba(239,68,68,0.20)",
        clearBorder: "rgba(239,68,68,0.20)",

        // ── Checkbox ─────────────────────────────────────────────────────────
        // mutedText dark at 30%
        checkBorder: "rgba(148,163,184,0.30)",
        // velBlue at 60%
        checkBorderActive: "rgba(14,165,233,0.60)",
        // velBlue at 20%
        checkBgActive: "rgba(14,165,233,0.20)",
      }
    : {
        // ── Surface ──────────────────────────────────────────────────────────
        // surface light (#e4eaf2) at 92% — cool blue-gray, NOT white
        barBg: "rgba(228,234,242,0.92)",
        // surfaceBorder light (#a0afc2) at 60%
        barBorder: "rgba(160,175,194,0.60)",
        // shadow-login-panel light
        barShadow: "0 12px 50px rgba(0,0,0,0.18), 0 0 1px rgba(0,0,0,0.05)",
        // surfaceBorder light at 45%
        divider: "rgba(160,175,194,0.45)",

        // ── Inputs & buttons ─────────────────────────────────────────────────
        // inputBg light (#dfe6ef) at 80%
        inputBg: "rgba(223,230,239,0.80)",
        // inputBorder light (#788698) at 55% — VN input border family
        inputBorder: "rgba(120,134,152,0.55)",
        // mainText light
        inputText: "rgba(15,23,42,0.88)",
        // mutedText light at 60% — acceptable for placeholders
        inputPlaceholder: "rgba(100,116,139,0.60)",

        // ── Pills (inactive filter buttons) ──────────────────────────────────
        // inputBg light at 80%
        pillBg: "rgba(223,230,239,0.80)",
        // surfaceBorder light at 45%
        pillBorder: "rgba(160,175,194,0.45)",
        // mutedText light at 70%
        pillText: "rgba(100,116,139,0.70)",
        // mutedText light at 50%
        pillIconColor: "rgba(100,116,139,0.50)",

        // ── Active states ─────────────────────────────────────────────────────
        // velBlue at 10%
        activeBg: "rgba(14,165,233,0.10)",
        // velBlue at 30%
        activeBorder: "rgba(14,165,233,0.30)",
        // sky-600 (#0284c7) — darker velBlue for contrast on light canvas
        activeText: "rgba(2,132,199,1)",
        activeIcon: "rgba(2,132,199,0.85)",

        // ── Hover ─────────────────────────────────────────────────────────────
        // inputBg light full opacity — visible lift on surface
        hoverBg: "rgba(223,230,239,1.0)",
        // velBlue at 25%
        hoverBorder: "rgba(14,165,233,0.25)",
        // velBlue glow at 8%
        hoverShadow: "0 0 8px rgba(14,165,233,0.08)",
        hoverBtnShadow: "0 0 10px rgba(14,165,233,0.10)",

        // ── Text ──────────────────────────────────────────────────────────────
        // mainText light
        textPrimary: "rgba(15,23,42,0.88)",
        // mutedText light (#788698 family) at 80% — cool-toned, not warm slate
        textSecondary: "rgba(100,116,139,0.80)",
        // mutedText light at 55%
        textMuted: "rgba(100,116,139,0.55)",

        // ── Dropdown ─────────────────────────────────────────────────────────
        // popUpbg light (#edf1f7) at 98% — cool blue-gray, NOT white
        dropBg: "rgba(237,241,247,0.98)",
        // surfaceBorder light (#a0afc2) at 70%
        dropBorder: "rgba(160,175,194,0.70)",
        // shadow-login-panel light
        dropShadow: "0 12px 50px rgba(0,0,0,0.18), 0 0 1px rgba(0,0,0,0.05)",
        // selectedSection light (#bfcadb) at 60% — matches hover:bg-selectedSection
        dropHover: "rgba(191,202,219,0.60)",

        // ── Icon button default ───────────────────────────────────────────────
        // mutedText light at 60%
        iconDefault: "rgba(100,116,139,0.60)",

        // ── Clear / destructive button ────────────────────────────────────────
        // errorText / inactiveCase family — red, slightly darker in light mode
        clearText: "rgba(220,38,38,0.90)",
        clearBg: "rgba(239,68,68,0.08)",
        clearBgHover: "rgba(239,68,68,0.15)",
        clearBorder: "rgba(239,68,68,0.15)",

        // ── Checkbox ─────────────────────────────────────────────────────────
        // surfaceBorder light at 60%
        checkBorder: "rgba(160,175,194,0.60)",
        // velBlue at 50%
        checkBorderActive: "rgba(14,165,233,0.50)",
        // velBlue at 12%
        checkBgActive: "rgba(14,165,233,0.12)",
      };
